import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Check } from 'lucide-react';
import { 
  EmailConfig, 
  testEmailConnection, 
  saveConfigurations 
} from '@/utils/config';

const emailSchema = z.object({
  provider: z.enum(['smtp', 'sendgrid', 'mailgun', 'other']),
  host: z.string().optional(),
  port: z.coerce.number().int().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  encryption: z.enum(['none', 'tls', 'ssl']).optional(),
  apiKey: z.string().optional(),
  fromEmail: z.string().email('Invalid email address'),
  fromName: z.string().min(1, 'From name is required'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

interface EmailConfigFormProps {
  initialConfig?: EmailConfig | null;
  onSaved?: () => void;
}

const EmailConfigForm: React.FC<EmailConfigFormProps> = ({ 
  initialConfig = null,
  onSaved 
}) => {
  const { toast } = useToast();
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      provider: initialConfig?.provider || 'smtp',
      host: initialConfig?.host || '',
      port: initialConfig?.port || 587,
      username: initialConfig?.username || '',
      password: initialConfig?.password || '',
      encryption: initialConfig?.encryption || 'tls',
      apiKey: initialConfig?.apiKey || '',
      fromEmail: initialConfig?.fromEmail || '',
      fromName: initialConfig?.fromName || 'Referral Program',
    },
  });

  const handleTestConnection = async () => {
    const values = form.getValues();
    
    const result = await form.trigger();
    if (!result) {
      toast({
        title: "Form Validation Error",
        description: "Please fill all required fields correctly before testing.",
        variant: "destructive",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    
    try {
      const configToTest: EmailConfig = {
        provider: values.provider,
        fromEmail: values.fromEmail,
        fromName: values.fromName,
        testStatus: 'pending'
      };
      
      if (values.provider === 'smtp') {
        configToTest.host = values.host;
        configToTest.port = values.port;
        configToTest.username = values.username;
        configToTest.password = values.password;
        configToTest.encryption = values.encryption;
      } else {
        configToTest.apiKey = values.apiKey;
      }
      
      const result = await testEmailConnection(configToTest);
      setTestResult(result);
      
      toast({
        title: result.success ? "Connection Successful" : "Connection Failed",
        description: result.message,
        variant: result.success ? "default" : "destructive",
      });
    } catch (error) {
      toast({
        title: "Connection Test Error",
        description: error instanceof Error ? error.message : "Failed to test connection",
        variant: "destructive",
      });
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "An unknown error occurred",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const onSubmit = async (values: EmailFormValues) => {
    setIsSaving(true);
    
    try {
      const configToSave: EmailConfig = {
        provider: values.provider,
        fromEmail: values.fromEmail,
        fromName: values.fromName,
        testStatus: 'pending'
      };
      
      if (values.provider === 'smtp') {
        configToSave.host = values.host;
        configToSave.port = values.port;
        configToSave.username = values.username;
        configToSave.password = values.password;
        configToSave.encryption = values.encryption;
      } else {
        configToSave.apiKey = values.apiKey;
      }
      
      await saveConfigurations('email', configToSave);
      
      toast({
        title: "Configuration Saved",
        description: "Email configuration has been saved successfully.",
      });
      
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: "Save Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const watchProvider = form.watch('provider');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Mail className="mr-2 h-5 w-5" />
          Email Configuration
        </CardTitle>
        <CardDescription>
          Configure email service for sending notifications and referral invitations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Provider</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select email provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="smtp">SMTP Server</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="mailgun">Mailgun</SelectItem>
                      <SelectItem value="other">Other Provider</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Choose your email service provider
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchProvider === 'smtp' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="host"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SMTP Host</FormLabel>
                        <FormControl>
                          <Input placeholder="smtp.gmail.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="port"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SMTP Port</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="587"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Common ports: 25, 465 (SSL), 587 (TLS)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Username</FormLabel>
                        <FormControl>
                          <Input placeholder="your-email@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input 
                            type="password" 
                            placeholder="SMTP password or app password" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="encryption"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Encryption</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select encryption type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="tls">TLS</SelectItem>
                          <SelectItem value="ssl">SSL</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        The encryption method used by your SMTP server
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : (
              <FormField
                control={form.control}
                name="apiKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Key</FormLabel>
                    <FormControl>
                      <Input 
                        type="password" 
                        placeholder={`${watchProvider} API Key`} 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      The API key for your email service provider
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fromEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Email</FormLabel>
                    <FormControl>
                      <Input 
                        type="email" 
                        placeholder="referrals@yourcompany.com" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      The email address that will appear as the sender
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="fromName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Your Company Referrals" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      The name that will appear as the sender
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {testResult && (
              <div className={`p-3 rounded-md ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                <div className="flex items-center">
                  {testResult.success ? (
                    <Check className="h-5 w-5 mr-2 text-green-500" />
                  ) : (
                    <Mail className="h-5 w-5 mr-2 text-red-500" />
                  )}
                  <p>{testResult.message}</p>
                </div>
              </div>
            )}

            <CardFooter className="flex justify-between px-0">
              <Button 
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={isTesting}
              >
                {isTesting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing Connection
                  </>
                ) : (
                  "Test Connection"
                )}
              </Button>

              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving
                  </>
                ) : (
                  "Save Configuration"
                )}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default EmailConfigForm;
